import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import './admin.scss';
import { FiTrash2, FiEye, FiEyeOff, FiUpload, FiLogOut } from 'react-icons/fi';


const AdminDashboard = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery' or 'props'
  
  // Gallery states
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [uploadingImageTo, setUploadingImageTo] = useState(null);

  // Props states
  const [propsList, setPropsList] = useState([]);
  const [newPropTitle, setNewPropTitle] = useState('');
  const [newPropPrice, setNewPropPrice] = useState('');
  const [newPropImage, setNewPropImage] = useState(null);
  const newPropFileInputRef = useRef(null);
  
  const [editingPropId, setEditingPropId] = useState(null);
  const [editPropTitle, setEditPropTitle] = useState('');
  const [editPropPrice, setEditPropPrice] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
    fetchData();
    fetchPropsAgain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('gallery_sections')
      .select(`
        id, title, description, created_at,
        gallery_images (
          id, cloudinary_url, cloudinary_public_id, is_visible, created_at
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Error fetching data: ' + error.message);
    } else {
      // Sort images within sections by created_at
      data.forEach(section => {
        if(section.gallery_images) {
          section.gallery_images.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        }
      });
      setSections(data);
    }
    setLoading(false);
  };

  const fetchPropsAgain = async () => {
    const { data, error } = await supabase
      .from('explore_props')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to load props: ' + error.message);
    } else {
      setPropsList(data);
    }
  };

  const createSection = async (e) => {
    e.preventDefault();
    if (!newSectionTitle.trim()) return;

    const { error } = await supabase
      .from('gallery_sections')
      .insert([{ 
        title: newSectionTitle,
        description: newSectionDescription || null
      }])
      .select();

    if (error) {
      toast.error('Error creating album: ' + error.message);
    } else {
      toast.success('Album created successfully!');
      setNewSectionTitle('');
      setNewSectionDescription('');
      fetchData();
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Are you sure? This deletes the section and all its images from the DB.')) return;
    
    const { error } = await supabase
      .from('gallery_sections')
      .delete()
      .eq('id', id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Section deleted');
        fetchData();
      }
  };

  const toggleVisibility = async (image) => {
    const { error } = await supabase
      .from('gallery_images')
      .update({ is_visible: !image.is_visible })
      .eq('id', image.id);

    if (error) toast.error(error.message);
    else {
      toast.success(`Image ${image.is_visible ? 'hidden' : 'visible'}`);
      fetchData();
    }
  };

  const deleteImage = async (image) => {
    if (!window.confirm('Delete this image?')) return;

    const { error } = await supabase
      .from('gallery_images')
      .delete()
      .eq('id', image.id);

    if (error) {
      toast.error(error.message);
      return;
    }

    try {
      await supabase.functions.invoke('delete-cloudinary-image', {
        body: { public_id: image.cloudinary_public_id }
      });
    } catch (err) {
      console.warn('Edge function not deployed or failed. Image orphaned in Cloudinary.', err);
    }

    toast.success('Image deleted');
    fetchData();
  };

  const uploadImage = async (e, sectionId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImageTo(sectionId);
    
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
      toast.error('Cloudinary Env Variables are missing!');
      setUploadingImageTo(null);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.error) throw new Error(data.error.message);

      const { error } = await supabase
        .from('gallery_images')
        .insert([{
          section_id: sectionId,
          cloudinary_url: data.secure_url,
          cloudinary_public_id: data.public_id,
          is_visible: true
        }]);

      if (error) throw error;
      
      toast.success('Image uploaded!');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingImageTo(null);
    }
  };

  const savePropEdit = async (id) => {
    if (!editPropTitle.trim() || !editPropPrice.trim()) return;
    const { error } = await supabase
      .from('explore_props')
      .update({ title: editPropTitle, price: editPropPrice })
      .eq('id', id);

    if (error) {
      toast.error('Error updating prop: ' + error.message);
    } else {
      toast.success('Prop updated successfully!');
      setEditingPropId(null);
      fetchPropsAgain();
    }
  };

  if (loading && sections.length === 0 && propsList.length === 0) {
    return <div className="admin-loading">Loading Dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <Toaster />
      <header className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="header-right">
          <div className="admin-tabs">
            <button className={`tab-btn ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>Gallery</button>
            <button className={`tab-btn ${activeTab === 'props' ? 'active' : ''}`} onClick={() => setActiveTab('props')}>Props & Pricing</button>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FiLogOut /> Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {activeTab === 'gallery' && (
          <>
            <form onSubmit={createSection} className="create-section-form" style={{flexWrap: 'wrap'}}>
          <input 
            type="text" 
            placeholder="New Album Title (e.g. Odunlami 2025)" 
            value={newSectionTitle}
            onChange={(e) => setNewSectionTitle(e.target.value)}
            required
            style={{flex: '1 1 300px'}}
          />
          <input 
            type="text" 
            placeholder="Description (Optional)" 
            value={newSectionDescription}
            onChange={(e) => setNewSectionDescription(e.target.value)}
            style={{flex: '1 1 300px'}}
          />
          <button type="submit">Create Album</button>
        </form>

        <div className="sections-list">
          {sections.map(section => (
            <div key={section.id} className="admin-section">
              <div className="section-header">
                <div>
                <h2>{section.title}</h2>
                {section.description && <p style={{color: '#916859', fontSize: '1rem', marginTop: '4px'}}>{section.description}</p>}
              </div>
                <div className="section-actions">
                  <label className="upload-btn">
                    {uploadingImageTo === section.id ? 'Uploading...' : <><FiUpload /> Add Image</>}
                    <input 
                      type="file" 
                      accept="image/*,video/mp4" 
                      onChange={(e) => uploadImage(e, section.id)} 
                      disabled={uploadingImageTo === section.id}
                      style={{display: 'none'}}
                    />
                  </label>
                  <button className="delete-section-btn" onClick={() => deleteSection(section.id)}>
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              <div className="admin-image-grid">
                {section.gallery_images && section.gallery_images.length > 0 ? (
                  section.gallery_images?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map(img => (
                    <div 
                      key={img.id} 
                      className={`admin-image-card ${!img.is_visible ? 'hidden-image' : ''}`}
                    >
                      <img 
                        src={img.cloudinary_url ? img.cloudinary_url.replace('/upload/', '/upload/q_auto,f_auto,w_400/') : ''} 
                        alt="Gallery" 
                      />
                      <div className="image-overlay">
                        <button onClick={() => toggleVisibility(img)} title="Toggle Visibility">
                          {img.is_visible ? <FiEye /> : <FiEyeOff />}
                        </button>
                        <button onClick={() => deleteImage(img)} title="Delete Image">
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-images">No images in this section yet.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
      )}

        {activeTab === 'props' && (
          <div className="props-admin-section">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newPropTitle.trim() || !newPropPrice.trim()) return;
              
              setLoading(true);
              let imageUrl = null;
              let imagePublicId = null;

              if (newPropImage) {
                 try {
                   const formData = new FormData();
                   formData.append('file', newPropImage);
                   formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
                   const response = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: 'POST', body: formData });
                   const data = await response.json();
                   if (data.secure_url) {
                      imageUrl = data.secure_url;
                      imagePublicId = data.public_id;
                   } else {
                      throw new Error('Image upload failed');
                   }
                 } catch (err) {
                   toast.error('Failed to upload image. Prop not created.');
                   setLoading(false);
                   return;
                 }
              }

              const { error } = await supabase.from('explore_props').insert([{ 
                  title: newPropTitle, 
                  price: newPropPrice,
                  cloudinary_url: imageUrl,
                  cloudinary_public_id: imagePublicId
              }]);

              if (error) {
                 toast.error('Error creating prop: ' + error.message);
              } else { 
                 toast.success('Prop created successfully!'); 
                 setNewPropTitle(''); 
                 setNewPropPrice(''); 
                 setNewPropImage(null);
                 if (newPropFileInputRef.current) newPropFileInputRef.current.value = "";
                 fetchPropsAgain(); 
              }
              setLoading(false);
            }} className="create-section-form" style={{flexWrap: 'wrap', alignItems: 'stretch', gap: '1rem'}}>
              <input type="text" placeholder="Prop Title (e.g. 360 Photo Booth)" value={newPropTitle} onChange={(e) => setNewPropTitle(e.target.value)} required style={{flex: '1 1 200px'}} />
              <input type="text" placeholder="Price (e.g. $150/hr)" value={newPropPrice} onChange={(e) => setNewPropPrice(e.target.value)} required style={{flex: '1 1 200px'}} />
              
              <label style={{ flex: '1 1 250px', padding: '1rem', border: '2px dashed rgba(145,104,89,0.5)', borderRadius: '6px', textAlign: 'center', cursor: 'pointer', color: '#916859', background: '#fdfdfd', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseOver={e => e.currentTarget.style.borderColor = '#916859'} onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(145,104,89,0.5)'}>
                <span style={{fontWeight: 'bold'}}>{newPropImage ? newPropImage.name : '+ Select Image'}</span>
                <input type="file" accept="image/*" ref={newPropFileInputRef} onChange={(e) => setNewPropImage(e.target.files[0])} hidden />
              </label>

              <button type="submit" disabled={loading} style={{flex: '1 1 150px'}}>{loading ? 'Creating...' : 'Create Prop'}</button>
            </form>

            <div className="sections-list">
              <div className="admin-section">
                <div className="section-header">
                  <h2>Active Props</h2>
                </div>
                <div className="admin-image-grid">
                   {propsList.map(prop => (
                      <div key={prop.id} className="admin-image-card" style={{display: 'flex', flexDirection: 'column', height: 'auto', aspectRatio: 'auto'}}>
                         <div style={{height: '200px', width: '100%', position: 'relative', background: '#eee'}}>
                           {prop.cloudinary_url ? (
                               <img src={prop.cloudinary_url.replace('/upload/', '/upload/q_auto,f_auto,w_400/')} alt="prop" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                           ) : (
                               <div style={{background: '#eee', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#916859'}}>No Image</div>
                           )}
                           <div className="image-overlay" style={{padding: '1rem', background: 'rgba(251, 244, 229, 0.95)'}}>
                               <div style={{display: 'flex', gap: '1rem'}}>
                                   {!prop.cloudinary_url && (
                                      <label className="upload-btn" style={{margin: 0, fontSize: '0.8rem', padding: '0.5rem', cursor: 'pointer', background: '#fff', border: '1px solid #916859', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                         {uploadingImageTo === prop.id ? '...' : <FiUpload color="#916859" />}
                                         <input type="file" hidden accept="image/*" onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            setUploadingImageTo(prop.id);
                                            try {
                                              const formData = new FormData();
                                              formData.append('file', file);
                                              formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
                                              const response = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: 'POST', body: formData });
                                              const data = await response.json();
                                              if (data.secure_url) {
                                                await supabase.from('explore_props').update({ cloudinary_url: data.secure_url, cloudinary_public_id: data.public_id }).eq('id', prop.id);
                                                toast.success('Prop image uploaded!');
                                                fetchPropsAgain();
                                              }
                                            } catch (err) { toast.error('Failed to upload image.'); } finally { setUploadingImageTo(null); }
                                         }} />
                                      </label>
                                   )}
                                   <button onClick={async () => {
                                      if (!window.confirm(`Delete ${prop.title}?`)) return;
                                      setLoading(true);
                                      if (prop.cloudinary_public_id) {
                                        try {
                                          const { data: { session } } = await supabase.auth.getSession();
                                          await fetch('https://ggexnliypymgxzhwhfsf.supabase.co/functions/v1/delete-cloudinary-image', {
                                            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                                            body: JSON.stringify({ public_id: prop.cloudinary_public_id })
                                          });
                                        } catch (err) { console.error(err); }
                                      }
                                      await supabase.from('explore_props').delete().eq('id', prop.id);
                                      toast.success('Prop deleted');
                                      fetchPropsAgain();
                                      setLoading(false);
                                   }} title="Delete Prop"><FiTrash2 /></button>
                               </div>
                           </div>
                         </div>
                         <div style={{padding: '1rem', background: '#fff', display: 'flex', flexDirection: 'column', gap: '0.8rem', borderTop: '1px solid rgba(145,104,89,0.1)'}}>
                            {editingPropId === prop.id ? (
                               <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                                  <input type="text" value={editPropTitle} onChange={e => setEditPropTitle(e.target.value)} style={{width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px'}} />
                                  <input type="text" value={editPropPrice} onChange={e => setEditPropPrice(e.target.value)} style={{width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px'}} />
                                  <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                                    <button onClick={() => savePropEdit(prop.id)} style={{background: '#916859', color: '#fff', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer', flex: 1}}>Save</button>
                                    <button onClick={() => setEditingPropId(null)} style={{background: 'transparent', color: '#916859', border: '1px solid #916859', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', flex: 1}}>Cancel</button>
                                  </div>
                               </div>
                            ) : (
                               <>
                                  <h3 style={{color: '#916859', fontSize: '1.2rem', margin: 0}}>{prop.title}</h3>
                                  <p style={{color: '#666', margin: 0}}>{prop.price}</p>
                                  <button onClick={() => {
                                     setEditingPropId(prop.id);
                                     setEditPropTitle(prop.title);
                                     setEditPropPrice(prop.price);
                                  }} style={{background: 'transparent', border: '1px solid #916859', color: '#916859', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', marginTop: '0.5rem', fontWeight: 'bold'}}>Edit Details</button>
                               </>
                            )}
                         </div>
                      </div>
                   ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
